<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>OpenVPN 集中管理</strong>
        <small class="text-muted ml-2">一台路由器作 ovpn-server，其余作客户端接入；统一 CA + 每台独立客户端证书；互联 IP 取自 Link IP 池（/30）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <!-- Server -->
        <CAlert v-if="!loading && !server" color="info" fade>
          尚未初始化。选择一台路由器作为 OpenVPN 服务器端：系统将在线生成 CA 与服务器证书、下发到该路由器并启用 ovpn-server。
        </CAlert>
        <div v-if="server" class="openvpn-server-card">
          <div class="openvpn-server-header">
            <strong>{{ server.router_name }}</strong>
            <small class="text-muted ml-2">{{ server.router_host }}</small>
            <CBadge color="light" class="ml-2">{{ server.router_version }}</CBadge>
            <CBadge color="info" class="ml-2">{{ server.protocol.toUpperCase() }}:{{ server.port }}</CBadge>
            <CBadge color="dark" class="ml-2">{{ server.cipher }}</CBadge>
            <CBadge :color="server.enabled ? 'success' : 'secondary'" class="ml-1">
              {{ server.enabled ? '启用' : '禁用' }}
            </CBadge>
            <span class="ml-auto">
              <CButtonGroup size="sm">
                <CButton color="info" size="sm" @click="openServerModal">编辑</CButton>
                <CButton color="success" size="sm" :disabled="statusLoading" @click="loadStatus">在线状态</CButton>
              </CButtonGroup>
            </span>
          </div>
          <div class="openvpn-server-meta text-muted">
            <span>CA: <code>{{ server.ca_cn }}</code>
              <CBadge :color="daysColor(server.ca_days_left)" class="ml-1">{{ server.ca_days_left }} 天</CBadge>
            </span>
            <span class="ml-3">Server 证书: <code>{{ server.server_cert_cn }}</code>
              <CBadge :color="daysColor(server.server_cert_days_left)" class="ml-1">{{ server.server_cert_days_left }} 天</CBadge>
            </span>
          </div>
        </div>

        <hr class="my-3" />

        <!-- Clients -->
        <div class="d-flex mb-2">
          <CCardText class="mb-0"><strong>Clients（{{ clients.length }}）</strong>
            <small class="text-muted ml-2">每台路由器独立客户端证书；接口与 /30 互联地址下发后，可在 OSPF/BGP 页面直接在该链路上配路由</small>
          </CCardText>
          <CButton color="primary" size="sm" class="ml-auto" :disabled="!server" @click="openAddModal">添加客户端</CButton>
        </div>
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th>路由器</th>
              <th>接口</th>
              <th>互联 /30（server ↔ client）</th>
              <th>证书</th>
              <th width="90">状态</th>
              <th width="260">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in clients" :key="c.id" :class="{ 'text-muted': !c.enabled }">
              <td><strong>{{ c.router_name }}</strong></td>
              <td><code>{{ c.interface_name }}</code></td>
              <td>
                <code>{{ c.link_ip }}</code>
                <small class="text-muted">（{{ c.server_ip }} ↔ {{ c.local_ip }}）</small>
              </td>
              <td>
                <code>{{ c.cert_cn }}</code>
                <CBadge :color="daysColor(c.cert_days_left)" class="ml-1">{{ c.cert_days_left }} 天</CBadge>
              </td>
              <td>
                <CBadge :color="c.enabled ? 'success' : 'secondary'">{{ c.enabled ? '启用' : '禁用' }}</CBadge>
                <CBadge v-if="!c.pushed" color="warning" class="ml-1">未下发</CBadge>
              </td>
              <td>
                <CButtonGroup size="sm">
                  <CButton color="info" size="sm" @click="repush(c)">重下发</CButton>
                  <CButton color="warning" size="sm" @click="toggleClient(c)">{{ c.enabled ? '禁用' : '启用' }}</CButton>
                  <CButton color="success" size="sm" @click="renewCert(c)">重签证书</CButton>
                  <CButton color="danger" size="sm" @click="removeClient(c)">删除</CButton>
                </CButtonGroup>
              </td>
            </tr>
            <tr v-if="clients.length === 0">
              <td colspan="6" class="text-center text-muted">暂无客户端</td>
            </tr>
          </tbody>
        </table>

        <!-- Sessions -->
        <div v-if="sessions.length > 0" class="mt-3">
          <CCardText><strong>在线 Session（来自 server /ppp/active）</strong></CCardText>
          <table class="table table-sm">
            <thead><tr><th>用户</th><th>地址</th><th>uptime</th><th>来源</th></tr></thead>
            <tbody>
              <tr v-for="s in sessions" :key="s.username">
                <td><code>{{ s.username }}</code></td>
                <td>{{ s.address }}</td>
                <td>{{ s.uptime }}</td>
                <td>{{ s.caller_id }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- Server Modal -->
    <CModal :show.sync="serverModal.show" :title="server ? '编辑 OpenVPN Server' : '初始化 OpenVPN Server'" color="info">
      <CForm>
        <CAlert v-if="!server" color="info" fade>
          初始化时将在线生成 CA（10 年）与服务器证书（825 天），通过 API 导入该路由器并启用 ovpn-server。统一 CA 之后会下发到每台客户端路由器。
        </CAlert>
        <div class="form-group">
          <label class="form-label">Server 路由器</label>
          <SearchSelect :options="routerOptions" v-model="serverModal.form.router_id" placeholder="输入路由器名筛选" />
        </div>
        <CRow>
          <CCol sm="6">
            <CInput label="端口" v-model="serverModal.form.port" placeholder="1194" />
          </CCol>
          <CCol sm="6">
            <CSelect label="协议" v-model="serverModal.form.protocol" :options="[
              { value: 'tcp', label: 'TCP（兼容所有版本）' },
              { value: 'udp', label: 'UDP（需 RouterOS 7.13+）' }
            ]" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="6">
            <CSelect label="Cipher" v-model="serverModal.form.cipher" :options="[
              { value: 'aes-256-gcm', label: 'aes-256-gcm（推荐, 7.6+）' },
              { value: 'aes-256-cbc', label: 'aes-256-cbc' }
            ]" />
          </CCol>
          <CCol sm="6">
            <CSelect label="Auth" v-model="serverModal.form.auth" :options="[
              { value: 'sha256', label: 'sha256' },
              { value: 'sha512', label: 'sha512' }
            ]" />
          </CCol>
        </CRow>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="serverModal.show = false">取消</CButton>
          <CButton color="success" :disabled="serverModal.loading" @click="saveServer">
            {{ serverModal.loading ? '下发中...' : (server ? '保存' : '初始化') }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Add Client Modal -->
    <CModal :show.sync="addModal.show" title="添加 OpenVPN 客户端" color="primary">
      <CForm>
        <CAlert color="info" fade>
          流程：自动从 Link IP 池分配 /30（全局池优先）→ 签发该路由器独立客户端证书 →
          server 侧创建 secret（local-address=.1）→ 客户端路由器导入 CA+证书并创建 ovpn-client（地址 .2）。
        </CAlert>
        <div class="form-group">
          <label class="form-label">客户端路由器</label>
          <SearchSelect :options="clientRouterOptions" v-model="addModal.form.router_id" placeholder="输入路由器名筛选" />
        </div>
        <CSelect label="Link IP 池（可选, 留空自动选择全局池）" v-model="addModal.form.pool_id" :options="poolOptions" placeholder="（自动）" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="addModal.show = false">取消</CButton>
          <CButton color="success" :disabled="addModal.loading" @click="addClient">{{ addModal.loading ? '下发中...' : '创建' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <CModal title="Message" color="primary" :show.sync="msgModal">
      {{ msg }}
      <template #footer>
        <CButton @click="msgModal=false" color="primary">Close</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import SearchSelect from '@/components/SearchSelect'

export default {
  name: 'OpenVpnList',
  components: { SearchSelect },
  data() {
    return {
      loading: false,
      statusLoading: false,
      server: null,
      clients: [],
      sessions: [],
      allRouters: [],
      pools: [],
      msg: '',
      msgModal: false,
      serverModal: {
        show: false,
        loading: false,
        form: { router_id: '', port: '1194', protocol: 'tcp', cipher: 'aes-256-gcm', auth: 'sha256' }
      },
      addModal: {
        show: false,
        loading: false,
        form: { router_id: '', pool_id: '' }
      }
    }
  },
  computed: {
    routerOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: `${r.name} (${r.host})` }))
    },
    clientRouterOptions() {
      const serverId = this.server ? this.server.router_id : null
      const existing = new Set(this.clients.map(c => Number(c.router_id)))
      return this.allRouters
        .filter(r => r.id != serverId && !existing.has(Number(r.id)))
        .map(r => ({ value: r.id, label: `${r.name} (${r.host})` }))
    },
    poolOptions() {
      // 仅 v4 池
      return this.pools
        .filter(p => !(p.network || '').includes(':'))
        .map(p => ({ value: p.id, label: p.network + (p.region_id === 0 ? '（全局）' : '') }))
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
    this.loadPools()
  },
  methods: {
    daysColor(days) {
      if (days === null || days === undefined) return 'secondary'
      if (days < 30) return 'danger'
      if (days < 90) return 'warning'
      return 'success'
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/openvpn/list')
        if (res.data.code === 200) {
          this.server = res.data.data.server
          this.clients = res.data.data.clients || []
        } else {
          this.showMsg(res.data.msg || '加载失败')
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
      this.loading = false
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadPools() {
      try {
        const res = await this.$axios.get('user/link-ip/list')
        if (res.data.code === 200) {
          this.pools = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadStatus() {
      this.statusLoading = true
      try {
        const res = await this.$axios.get('user/openvpn/status')
        if (res.data.code === 200) {
          this.sessions = res.data.data.sessions || []
          if (this.sessions.length === 0) {
            this.showMsg('当前无在线 session')
          }
        } else {
          this.showMsg(res.data.msg || '查询失败')
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
      this.statusLoading = false
    },
    openServerModal() {
      this.serverModal.form = {
        router_id: this.server ? this.server.router_id : '',
        port: this.server ? String(this.server.port) : '1194',
        protocol: this.server ? this.server.protocol : 'tcp',
        cipher: this.server ? this.server.cipher : 'aes-256-gcm',
        auth: this.server ? this.server.auth : 'sha256'
      }
      this.serverModal.show = true
    },
    async saveServer() {
      const f = this.serverModal.form
      if (!f.router_id) {
        this.showMsg('请选择 Server 路由器')
        return
      }
      this.serverModal.loading = true
      try {
        const res = await this.$axios.post('user/openvpn/server/save', f)
        if (res.data.code === 200) {
          this.$toasted.show('已保存并下发', { type: 'success' })
          this.serverModal.show = false
          this.loadList()
        } else {
          this.showMsg(res.data.msg || '保存失败')
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
      this.serverModal.loading = false
    },
    openAddModal() {
      this.addModal.form = { router_id: '', pool_id: '' }
      this.addModal.show = true
    },
    async addClient() {
      const f = this.addModal.form
      if (!f.router_id) {
        this.showMsg('请选择客户端路由器')
        return
      }
      this.addModal.loading = true
      try {
        const res = await this.$axios.post('user/openvpn/client/add', f)
        if (res.data.code === 200) {
          this.$toasted.show(res.data.msg + ` [${res.data.data.link_ip} ${res.data.data.server_ip} ↔ ${res.data.data.client_ip}]`, { type: 'success' })
          this.addModal.show = false
          this.loadList()
        } else {
          this.showMsg(res.data.msg || '创建失败')
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
      this.addModal.loading = false
    },
    async repush(c) {
      try {
        const res = await this.$axios.post(`user/openvpn/client/${c.id}/repush`)
        if (res.data.code === 200) {
          this.$toasted.show('已重新下发', { type: 'success' })
          this.loadList()
        } else {
          this.showMsg(res.data.msg)
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
    },
    async toggleClient(c) {
      try {
        const res = await this.$axios.post(`user/openvpn/client/${c.id}/toggle`)
        if (res.data.code === 200) {
          this.$toasted.show(c.enabled ? '已禁用' : '已启用', { type: 'success' })
          this.loadList()
        } else {
          this.showMsg(res.data.msg)
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
    },
    async renewCert(c) {
      if (!confirm(`确认为 "${c.router_name}" 重签客户端证书？（新有效期 825 天，自动下发到该路由器）`)) return
      try {
        const res = await this.$axios.post(`user/openvpn/cert/${c.cert_id}/renew`)
        this.showMsg(res.data.msg)
        if (res.data.code === 200) this.loadList()
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
    },
    async removeClient(c) {
      if (!confirm(`确认删除 OpenVPN 客户端 "${c.router_name}"？（server 侧 secret、客户端接口与证书、/30 互联分配将一并清理）`)) return
      try {
        const res = await this.$axios.delete(`user/openvpn/client/${c.id}/delete`)
        if (res.data.code === 200) {
          this.$toasted.show('已删除', { type: 'success' })
          this.loadList()
        } else {
          this.showMsg(res.data.msg)
        }
      } catch (e) {
        this.showMsg('网络错误: ' + e.message)
      }
    },
    showMsg(msg) {
      this.msg = msg
      this.msgModal = true
    }
  }
}
</script>

<style scoped>
.openvpn-server-card {
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.openvpn-server-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(69, 85, 100, 0.12);
  border-left: 4px solid #45556e;
  border-radius: 4px 4px 0 0;
}
.openvpn-server-meta {
  padding: 8px 12px;
}
</style>
